package Structs

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull

/**
 * Mobile JSON Authorization System — shared data models.
 *
 * These are pure data holders: kept here (next to [Agent]) so both the old
 * app and NiHulKavMobile can share the same wire format, but the resolution
 * logic (role fallbacks, [MobileAuthPolicy] evaluation) lives entirely in
 * NiHulKavMobile's `com.nihulkav.mobile.auth` package — the old app never
 * reads [Agent.mobileAuth] and keeps using [Agent.auth] / the legacy ints.
 *
 * Populated by a dedicated call to `/mobile_auth/resolve` after login/fetch
 * (see `MobileDB.fetchMobileAuthPolicy`), never from the `/login` or
 * `/agents` payload itself — see the migration file under
 * NihulKavRestApi/mysql_migrations for why the policy is intentionally kept
 * out of the `user_info` row.
 *
 * Sections mirror MobileAuthSchema.py on the backend:
 * - [MobileAuthPolicy.actions] — booleans; the first ten keys share their
 *   name with [Auth]'s fields so the effective [Auth] can be read directly.
 * - [MobileAuthPolicy.levels] — small integers (price_auth 0..3, change_auth 0..2).
 * - [MobileAuthPolicy.pages] — page/route visibility gates.
 * - [MobileAuthPolicy.visibility] — data visibility flags (prices/costs/discounts/…).
 * - [MobileAuthPolicy.settings] — UI defaults, each with a `locked` flag: when
 *   locked, the device/user-local preference is ignored and the admin value
 *   always applies; otherwise it's just the initial default.
 */
@Serializable
data class PolicySetting(
    val value: JsonElement = JsonNull,
    val locked: Boolean = false,
)

@Serializable
data class MobileAuthPolicy(
    val actions: Map<String, Boolean> = emptyMap(),
    val levels: Map<String, Int> = emptyMap(),
    val pages: Map<String, Boolean> = emptyMap(),
    val visibility: Map<String, Boolean> = emptyMap(),
    val settings: Map<String, PolicySetting> = emptyMap(),
)

/**
 * Envelope returned by `/mobile_auth/resolve` and cached on [Agent.mobileAuth]
 * for the whole session (and across offline logins, via the same on-disk
 * agent cache as everything else) — refreshed only on the next successful
 * online login, matching the requested "changes apply on next login" rollout.
 */
@Serializable
data class MobileAuthEnvelope(
    val schemaVersion: Int = 1,
    /** Role identity used to pick the built-in fallback — never a permission by itself. */
    val role: String = "readonly",
    val templateId: String? = null,
    /** "role_default" | "template" | "user_override" — surfaced for debugging/admin preview only. */
    val source: String = "role_default",
    val policy: MobileAuthPolicy = MobileAuthPolicy(),
)
